"use client";

import cn from "classnames";
import { Typography } from "@mui/material";
import { useSwiperSlide } from "swiper/react";

import { ImgBgResponsive } from "@/src/component/base/ImgBgResponsive";
import { DEEPKSY_IMGIX } from "@/src/const";
import { ProviderHooks } from "@/src/hook";
import { UnitDetailsLogic } from "@/src/logic";
import { StrapiCollectionType, StrapiComponentType } from "@/src/type";
import { LoadFromCDN, StringHelper } from "@/src/util";

import { TextModule, ButtonLinkModule } from "@/src/component/partial/__module__";

import styles from "./AvailableHomes.module.scss";
import { useMemo } from "react";

interface AvailableHomesCardProps {
  index: number;
  card: StrapiCollectionType.PropertyFloorplan;
  quality: number;
  lazy?: "eager" | "lazy";
  mobileHint?: boolean;
  total: number;
  deviceWidthHint?: number;
  backgroundImg?: string;
  cta?: StrapiComponentType.Element.ButtonLink;
  layout?: string;
  unitLinkActive?: boolean;
  swiperOn?: boolean;
  filtersQuery: string;
}

function AvailableHomesCard({
  card,
  quality,
  mobileHint,
  deviceWidthHint,
  backgroundImg,
  cta,
  layout,
  unitLinkActive,
  swiperOn,
  filtersQuery,
}: AvailableHomesCardProps) {
  const swiperSlide = useSwiperSlide();
  const showing = swiperOn ? swiperSlide.isActive || swiperSlide.isNext || swiperSlide.isPrev : true;
  const cms = ProviderHooks.useCMS();
  //const eagerMobile = (index ?? 0) < 2 || index + 1 === total || index === (total ?? 0) - 2 ? "eager" : "lazy";
  //const eagerDesktop = (index ?? 0) < 3 ? "eager" : "lazy";
  const _lazy = "eager"; //lazy === "lazy" ? "lazy" : mobileHint ? eagerMobile : eagerDesktop;
  const Tag = unitLinkActive ? "a" : "div";
  const title = `${(card.title ?? `${card.externalRefType}`).replaceAll(" ", "-")}`.toLowerCase();
  const tagProps = useMemo(() => {
    const base =
      "${propertySlug}/available-homes/" + `${title}/${card.externalRefType}/${card.externalRefId}${filtersQuery ? `?${filtersQuery}` : ""}`;

    return unitLinkActive ? { href: cms.constructLink(base) } : {};
  }, [unitLinkActive, cms, filtersQuery, card.externalRefType, card.externalRefId, title]);
  const altCardImg = card?.floorplan?.media?.[0]?.external?.sourceUrl ?? `${DEEPKSY_IMGIX}/${card.floorplan?.media?.[0]?.fid}`;
  const cardImg = card?.poster?.data?.attributes?.url ?? altCardImg;
  const availableUnits = ((card?.floorplan?.unitTypes ?? []).flatMap((unitType: any) => unitType.units) ?? []).length;
  const showAvailableButton = !cta && tagProps?.href && !card?.specialAvailable && !card?.furnishedAvailable && availableUnits > 0;

  const getBedroomsTranslation = () => {
    switch (card.bedrooms) {
      case 0:
        return "Studio";
      case 1:
        return "1 beds";
      case 2:
        return "2 beds";
      case 3:
        return "3 beds";
      case 4:
        return "4 beds";
      case 5:
        return "5 beds";
      default:
        return "";
    }
  };

  const getAreaUnit = () => {
    if (card.areaUnit === "sqft") {
      return "sqft";
    }
    return "m²";
  };

  const getPriceTerm = () => {
    if (card.startingAtPriceInterval === "month") {
      return "mo";
    }
    return "yr";
  };

  const getSpecialText = () => {
    if (card?.specialAvailable && card?.furnishedAvailable) {
      return "Furnished Specials";
    } else if (card?.furnishedAvailable) {
      return "Furnished Available";
    }
    return "Specials Available";
  };

  return (
    <Tag
      {...tagProps}
      className={cn(styles.cardWrapper)}
    >
      <ImgBgResponsive
        className={cn(styles.background, layout === "alternate" ? styles.alternate : undefined)}
        imageUrl={LoadFromCDN.imageQualityAndSizeOptional(backgroundImg ?? "", quality, mobileHint ? deviceWidthHint : undefined)}
        lazy={_lazy}
        prefetch={_lazy === "eager"}
        triggers={["swiperSlideChange", "click", "touchstart", "scroll"]}
      >
        {(card?.furnishedAvailable || card?.specialAvailable) && showing && (
          <div className={styles.specials}>
            <div className={styles.anchor}>
              <div className={styles.banner} />
              <div className={styles.corner} />
              <p className={styles.text}>{getSpecialText()}</p>
            </div>
          </div>
        )}
        {cta && (
          <div className={cn(styles.cta, card?.specialAvailable || card?.furnishedAvailable ? styles.right : undefined)}>
            <ButtonLinkModule
              buttonLink={cta}
              forceButton={true}
            />
          </div>
        )}
        {showAvailableButton && (
          <div className={styles.cta}>
            <ButtonLinkModule
              buttonLink={
                {
                  color: "primary",
                  variant: "outlined",
                  label: `${availableUnits} Available`,
                  target: "_self",
                  href: tagProps?.href,
                } as StrapiComponentType.Element.ButtonLink
              }
              forceButton={true}
            />
          </div>
        )}
        {cardImg && (
          <div className={styles.foreground}>
            <div className={styles.foregroundMessage} />
            <ImgBgResponsive
              className={styles.foregroundPoster}
              imageUrl={LoadFromCDN.imageQualityAndSizeOptional(cardImg, quality, 600)}
              lazy={_lazy}
              prefetch={_lazy === "eager"}
              triggers={["swiperSlideChange", "click", "touchstart", "scroll"]}
            />
          </div>
        )}
      </ImgBgResponsive>
      <div className={styles.textContainer}>
        <TextModule
          text={{
            tag: "body1",
            text: card.title,
          }}
          className={styles.cardTitle}
        />
        <Typography className={styles.price}>
          {card.startingAtPrice && (
            <>
              From {UnitDetailsLogic.priceFormatter(card.startingAtCurrency, card.startingAtPrice)} / {getPriceTerm()}
            </>
          )}
        </Typography>
        <div className={styles.facts}>
          <Typography>{getBedroomsTranslation()} | </Typography>
          <Typography>{card.bathrooms} baths | </Typography>
          <Typography>
            {StringHelper.addCommasToNumber(`${card.area}`)} {getAreaUnit()}
          </Typography>
        </div>
      </div>
    </Tag>
  );
}

export default AvailableHomesCard;
